// components/InAppBanner.tsx
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Collapse,
	IconButton,
	Stack,
	Tooltip
} from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { Notification } from 'resources/types'

interface InAppBannerProps {
	notification?: Notification
	onDismiss: (n: Notification) => void
	onView: (n: Notification) => void
	renderAction?: (n: Notification) => ReactNode
	maxWidth?: number | string
}

export function InAppBanner({
	notification,
	onDismiss,
	onView,
	renderAction,
	maxWidth = 1200,
}: InAppBannerProps) {
	const [open, setOpen] = useState(Boolean(notification))
	useEffect(() => setOpen(Boolean(notification)), [notification?.id])
	if (!notification) return null

	const icon = notification.kind === 'admin' ? <WarningAmberOutlinedIcon /> : <InfoOutlinedIcon />
	const severity = notification.kind === 'admin' ? 'warning' : 'info'

	const defaultAction =
		notification.action?.label && notification.action?.url ? (
			<Button
				size='small'
				href={notification.action.url}
				target={notification.action.url.startsWith('http') ? '_blank' : undefined}
				rel={notification.action.url.startsWith('http') ? 'noopener noreferrer' : undefined}
				onClick={() => onView(notification)}
			>
				{notification.action.label}
			</Button>
		) : (
			<Button
				size='small'
				href={`/notifications/${notification.id}`}
				onClick={() => onView(notification)}
			>
				View
			</Button>
		)

	return (
		<Collapse in={open} timeout={200} unmountOnExit>
			<Box sx={{ display: 'flex', justifyContent: 'center', px: 2 }}>
				<Alert
					icon={icon}
					severity={severity}
					sx={{ width: '100%', maxWidth, borderRadius: 2, boxShadow: 1 }}
					action={
						<Stack direction='row' spacing={1} alignItems='center'>
							{renderAction?.(notification) ?? defaultAction}
							<Tooltip
								title={notification.persistent ? 'Hide for this session' : 'Dismiss'}
							>
								<IconButton
									aria-label='dismiss'
									size='small'
									onClick={() => {
										setOpen(false)
										onDismiss(notification)
									}}
								>
									<CloseIcon fontSize='small' />
								</IconButton>
							</Tooltip>
						</Stack>
					}
				>
					<AlertTitle sx={{ fontWeight: 600 }}>
						<Box
							sx={{
								display: '-webkit-box',
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								WebkitLineClamp: 1, // show up to 3 lines
								whiteSpace: 'pre-line', // still respect \n within those lines
							}}
						>
							{notification.title}
						</Box>
					</AlertTitle>
					<Box
						sx={{
							display: '-webkit-box',
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							WebkitLineClamp: 3, // show up to 3 lines
							whiteSpace: 'pre-line', // still respect \n within those lines
						}}
					>
						{notification.body}
					</Box>
					{/* <AlertTitle sx={{ fontWeight: 600 }}>{notification.title}</AlertTitle>
					{notification.body} */}
				</Alert>
			</Box>
		</Collapse>
	)
}
